import type { FC, Ref } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, TextInput } from 'react-native';

import { MAX_MESSAGE_SIZE } from '@/features/chats/constants/limits';
import { usePopup } from '@/features/popup/provider/usePopup';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const VERTICAL_PADDING = 8;
const MAX_VISIBLE_LINES = 5;

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    input: {
      ...theme.fonts.bodyLarge,
      backgroundColor: theme.colors.surfaceContainerHigh,
      color: theme.colors.onSurfaceVariant,
      borderRadius: 28,
      paddingHorizontal: 16,
      minHeight: 40,
      maxHeight:
        theme.fonts.bodyLarge.lineHeight * MAX_VISIBLE_LINES +
        2 * VERTICAL_PADDING
    }
  });

type Props = {
  ref: Ref<TextInput | null>;
  value: string;
  onValueChanged: (text: string) => void;
  placeholder: string;
  autoFocus?: boolean;
  maxMessageLength?: number;
};

// TODO rename to GrowingTextInput and move to shared components
// TODO reset height
export const ChatMessageTextInput: FC<Props> = ({
  ref,
  value,
  onValueChanged,
  placeholder,
  autoFocus = false,
  maxMessageLength
}) => {
  const { showToast } = usePopup();
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const [height, setHeight] = useState(40);

  const { t } = useTranslation(LocalizationNameSpace.CHATS, {
    keyPrefix: 'components.chatLimit'
  });

  const heightStyle = {
    height: isEmptyOrNull(value) ? 40 : Math.max(height, 40)
  };
  const reachedLimit = value.length === (maxMessageLength ?? MAX_MESSAGE_SIZE);

  useEffect(() => {
    if (reachedLimit) {
      showToast({ message: t('message') });
    }
  }, [reachedLimit, showToast, t]);

  return (
    <TextInput
      // oxlint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={autoFocus}
      ref={ref}
      accessibilityRole="text"
      importantForAutofill="no"
      inputMode="text"
      maxLength={maxMessageLength ?? MAX_MESSAGE_SIZE}
      multiline
      placeholder={placeholder}
      placeholderTextColor={theme.colors.onSurfaceVariant}
      style={Platform.select({
        web: [
          styles.input,
          {
            outline: undefined,
            paddingVertical: VERTICAL_PADDING
          },
          heightStyle
        ],
        native: [styles.input, { overflow: 'hidden' }]
      })}
      value={value}
      onChangeText={onValueChanged}
      onContentSizeChange={(e) =>
        setHeight(
          Math.min(
            e.nativeEvent.contentSize.height,
            theme.fonts.bodyLarge.lineHeight * MAX_VISIBLE_LINES +
              2 * VERTICAL_PADDING
          )
        )
      }
    />
  );
};
